import { useNavigate } from "react-router-dom";
import { FormBackButton } from "@/ui/FormBackButton";
import { Button } from "@/ui/base/button";
import { Save } from "lucide-react";
import { cn } from "@/nucleo/utilitarios";

interface FormPageProps {
  /** Form submit handler */
  onSubmit: (e: React.FormEvent) => void;
  /** Whether the form is currently saving */
  isSaving?: boolean;
  /** Label for the submit button. Defaults to "Salvar" / "Salvando..." */
  submitLabel?: string;
  /** Icon for submit button. Defaults to Save */
  submitIcon?: React.ReactNode;
  /** Whether submit is disabled beyond isSaving */
  submitDisabled?: boolean;
  /** Max width class. Defaults to "max-w-3xl" */
  maxWidth?: string;
  /** Content rendered before the form (e.g. AutosaveBanner) */
  headerContent?: React.ReactNode;
  /** Custom back navigation path. If not provided, navigates -1 */
  backTo?: string;
  /** Hide the FormBackButton */
  hideBack?: boolean;
  /** Hide the default Cancel + Submit footer */
  hideFooter?: boolean;
  /** Custom cancel handler. Defaults to navigate(-1) */
  onCancel?: () => void;
  /** Form children (FormSections, Cards, etc.) */
  children: React.ReactNode;
}

export function FormPage({
  onSubmit,
  isSaving = false,
  submitLabel,
  submitIcon,
  submitDisabled = false,
  maxWidth = "max-w-3xl",
  headerContent,
  backTo,
  hideBack = false,
  hideFooter = false,
  onCancel,
  children,
}: FormPageProps) {
  const navigate = useNavigate();

  const handleCancel = onCancel ?? (() => {
    if (backTo) navigate(backTo);
    else navigate(-1);
  });

  const savingLabel = submitLabel
    ? (isSaving ? submitLabel.replace(/^[A-Z]/, (c) => c) + "..." : submitLabel)
    : (isSaving ? "Salvando..." : "Salvar");

  return (
    <div className={cn("mx-auto space-y-6", maxWidth)}>
      {!hideBack && <FormBackButton to={backTo} />}

      {headerContent}

      <form onSubmit={onSubmit} className="space-y-6">
        {children}

        {!hideFooter && (
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSaving || submitDisabled}>
              {submitIcon ?? <Save className="h-4 w-4 mr-2" />}
              {savingLabel}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
